import {
  BayLcgVcgTcgAndPairings,
  IBayPattern,
  TGetContainersCgsResult,
  calculateTeusFromSlots,
  getIsoBaysUnderLid,
  getPairedIsoBay,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  BayLevelEnum,
  ForeAftEnum,
  IBayLevelData,
  IBayLevelOffsetBottomBase,
  IFeaturesAllowed,
  ILidData,
  IMasterCGs,
  IOpenVesselDefinitionV1,
  IShipData,
  ISizeSummary,
  PositionFormatEnum,
  TBayRowInfo,
  TContainerLengths,
  TRowInfoByLength,
  ValuesSourceEnum,
} from "open-vessel-definition";
import {
  IFileState,
  LogEventEntitiesEnum,
  LogEventTypesEnum,
  TSourceFileCreated,
} from "@tedivo/tvd-api-models";
import {
  IRepairOvdResult,
  OvdValidator,
  fixMissingBaysData,
  repairOvd,
} from "@tedivo/tedivo-ovd-validators";
import {
  arraysAreEqual,
  cloneObject,
  diffArrays,
  objectsAreEqual,
  pad3,
  roundDec,
  sanitizeString,
  sortByMultipleFields,
} from "@tedivo/tedivo-pure-helpers";

import BeaconServices from "../beaconServices";
import { IBayLevelPairing } from "../../components/features/view-json/parts/edits/createPairingsEdit/IBayLevelPairing";
import { IResponseModel } from "../services/HttpClient";
import { IVesselOneParametrizationFormElements } from "@tedivo/tedivo-3d-visualizer";
import { IVesselParts } from "open-vessel-definition";
import Services from "../services";
import { errorTracking } from "../tracking/errorTracking";
import { mergeOVDChanges } from "./mergeOVDChanges";
import { safeSet } from "./safeSet";
import securityModule from "../security/SecurityModule";

const TypedOvdJsonStoreEventTarget = EventTarget as {
  new (): IOvdJsonStoreEventTarget;
  prototype: IOvdJsonStoreEventTarget;
};

export class OVDJsonStore extends TypedOvdJsonStoreEventTarget {
  private internalJson: IOpenVesselDefinitionV1 | undefined = undefined;
  private originalInternalJson: IOpenVesselDefinitionV1 | undefined = undefined;
  public internalName: string | undefined = undefined;
  private internalCloudInfo: ITvdCloudFileMeta | undefined = undefined;

  private _lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings | undefined = undefined;

  private _timeoutEmitter: number | undefined = undefined;
  /** Changes debounced into the next dispatch, merged so none of their Bays is lost */
  private _pendingChange: ICustomOVDChangeEvent | undefined = undefined;

  public readonlyMode = false;
  public ovdValidator = new OvdValidator();

  /** The repairs applied to the file as it was opened. See `repairOvd` */
  public lastRepair: IRepairOvdResult | undefined = undefined;

  public containersWithPositions: TGetContainersCgsResult | undefined = undefined;

  setJson(shipName: string, json: IOpenVesselDefinitionV1 | undefined) {
    this.internalName = shipName;
    this.lastRepair = undefined;

    this.internalJson = json === undefined ? undefined : cloneObject(fixMissingBaysData(json));

    this.originalInternalJson =
      json === undefined ? undefined : cloneObject(fixMissingBaysData(json));

    if (json !== undefined && this.internalJson !== undefined) {
      Object.freeze(this.originalInternalJson);

      // Data that cannot be drawn (row 00 slots in a Bay without the row, 40'+ in the
      // wrong Bay of a pair) is repaired as the file opens, so it never breaks the CGs.
      // The original stays as loaded; the user is told through "ovdRepaired".
      const repair = repairOvd(this.internalJson);
      if (repair.changed) {
        this.internalJson = repair.json;
        this.lastRepair = repair;
      }

      const jsonToAnalyse = repair.changed ? this.internalJson : json;

      this._lcgVcgTcgAndPairings = this.createLcgVcgTcgAndPairings(jsonToAnalyse);
      this.ovdValidator.validate(jsonToAnalyse, this._lcgVcgTcgAndPairings, undefined);
    }

    errorTracking.leaveBreadcrumb(
      `JSON "${this.internalName}" total slots defined: ${this.totalSlotsDefined}`,
    );

    this.dispatchEvent(new CustomEvent("jsonLoaded"));

    if (this.lastRepair !== undefined) {
      const summary = this.lastRepair.steps
        .filter((step) => step.changed)
        .map((step) => `${step.name}: ${step.count}`)
        .join(", ");
      errorTracking.leaveBreadcrumb(`JSON "${this.internalName}" repaired on open: ${summary}`);

      if (this.lastRepair.notable) {
        this.dispatchEvent(
          new CustomEvent<IRepairOvdResult>("ovdRepaired", { detail: this.lastRepair }),
        );
      }
    }

    return this;
  }

  /**
   * The CG pipeline assumes a well-formed file. When a structure validator reports an
   * error, or the pipeline throws, there are no CGs and the view shows the validation
   * results instead of crashing.
   */
  private createLcgVcgTcgAndPairings(
    json: IOpenVesselDefinitionV1,
  ): BayLcgVcgTcgAndPairings | undefined {
    const structureErrors = OvdValidator.validateStructure(json).filter(
      (r) => r.severity === "error",
    );

    if (structureErrors.length > 0) {
      errorTracking.leaveBreadcrumb(
        `JSON "${this.internalName}" has structure errors: ${structureErrors.map((r) => r.name).join(", ")}`,
      );
      return undefined;
    }

    try {
      return new BayLcgVcgTcgAndPairings(json);
    } catch (e) {
      errorTracking.leaveBreadcrumb(
        `JSON "${this.internalName}" CGs could not be calculated: ${String(e)}`,
      );
      return undefined;
    }
  }

  setLpp(lpp: number) {
    if (this.internalJson === undefined) return this;

    if (this.internalJson.shipData?.lcgOptions?.lpp === lpp) return this;

    safeSet<IShipData>(this.internalJson.shipData, "lcgOptions.lpp", lpp);
    this.emitChange({ type: OVDChangesEnum.SHIP_SIZE });

    return this;
  }

  setCgOptions(
    lcgValues?: ValuesSourceEnum,
    tcgValues?: ValuesSourceEnum,
    vcgValues?: ValuesSourceEnum,
    loa?: number,
    sternToAftPp?: number,
    lpp?: number,
  ) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    interface IEditGeneralFields {
      lcgValues?: ValuesSourceEnum;
      tcgValues?: ValuesSourceEnum;
      vcgValues?: ValuesSourceEnum;
      loa?: number;
      sternToAftPp?: number;
      lpp?: number;
    }

    const oldData: IEditGeneralFields = {
      lcgValues: this.internalJson.shipData.lcgOptions.values,
      tcgValues: this.internalJson.shipData.tcgOptions.values,
      vcgValues: this.internalJson.shipData.vcgOptions.values,
      loa: this.internalJson.shipData.loa,
      sternToAftPp: this.internalJson.shipData.sternToAftPp,
      lpp: this.internalJson.shipData.lcgOptions.lpp,
    };

    const newData: IEditGeneralFields = {
      lcgValues,
      tcgValues,
      vcgValues,
      loa,
      sternToAftPp,
      lpp,
    };

    const isDataEqual = objectsAreEqual<IEditGeneralFields>(oldData, newData);

    if (isDataEqual) {
      return this;
    }

    if (lcgValues !== undefined) {
      safeSet<IShipData>(this.internalJson.shipData, "lcgOptions.values", lcgValues);
    }

    if (tcgValues !== undefined) {
      safeSet<IShipData>(this.internalJson.shipData, "tcgOptions.values", tcgValues);
    }

    if (vcgValues !== undefined) {
      safeSet<IShipData>(this.internalJson.shipData, "vcgOptions.values", vcgValues);
    }

    if (loa !== undefined) {
      safeSet<IShipData>(this.internalJson.shipData, "loa", loa);
    }

    if (sternToAftPp !== undefined) {
      safeSet<IShipData>(this.internalJson.shipData, "sternToAftPp", sternToAftPp);
    }

    if (lpp !== undefined) safeSet<IShipData>(this.internalJson.shipData, "lcgOptions.lpp", lpp);

    this.emitChange({ type: OVDChangesEnum.FULL });

    BeaconServices.logEvents.notifyXhttp({
      eventEntity: LogEventEntitiesEnum.File,
      eventType: LogEventTypesEnum.Modified,
      subEvent: "CGsOptions",
      itemId: this.internalCloudInfo.fileId,
      ...securityModule.getBeaconMetadata(),
    });

    return this;
  }

  setShipDataGeneralInfo(
    {
      lineOperator,
      shipClass,
      shipName,
      callSign,
      imoCode,
      positionFormat,
      containersLengths,
      featuresAllowed: fA,
      yearBuilt,
      loa,
      shipNameAkaStr,
      bay40sLocation,
    }: Partial<IEditGeneralFields>,
    doEmitChange = true,
  ) {
    if (this.internalJson === undefined) return this;

    const featuresAllowed: IFeaturesAllowed = fA
      ? {
          slotCoolStowProhibited: fA.indexOf("slotCoolStowProhibited") >= 0,
          slotHazardousProhibited: fA.indexOf("slotHazardousProhibited") >= 0,
          slotConeRequired: fA.indexOf("slotConeRequired") >= 0,
        }
      : {
          slotCoolStowProhibited: false,
          slotHazardousProhibited: false,
          slotConeRequired: false,
        };

    const shipNameAkas =
      shipNameAkaStr
        ?.replace(/,/i, "\n")
        .split("\n")
        .map((s) => s.trim().toUpperCase()) || [];

    type TSubIShipData = Omit<
      IShipData,
      "lcgOptions" | "vcgOptions" | "tcgOptions" | "masterCGs" | "metaInfo"
    >;

    const newShipData: TSubIShipData = {
      lineOperator,
      shipClass: shipClass || "",
      shipName,
      callSign,
      imoCode,
      positionFormat: positionFormat || PositionFormatEnum.BAY_STACK_TIER,
      containersLengths: containersLengths || [],
      featuresAllowed,
      yearBuilt,
      loa,
      shipNameAkas,
      bay40sLocation,
    };

    const oldSubShipData: TSubIShipData = {
      lineOperator: this.internalJson.shipData?.lineOperator,
      shipClass: this.internalJson.shipData?.shipClass || "",
      shipName: this.internalJson.shipData?.shipName,
      callSign: this.internalJson.shipData?.callSign,
      imoCode: this.internalJson.shipData?.imoCode,
      bay40sLocation: this.internalJson.shipData?.bay40sLocation,
      positionFormat:
        this.internalJson.shipData?.positionFormat || PositionFormatEnum.BAY_STACK_TIER,
      containersLengths: this.internalJson.shipData?.containersLengths || [],
      featuresAllowed: this.internalJson.shipData?.featuresAllowed || {
        slotCoolStowProhibited: false,
        slotHazardousProhibited: false,
        slotConeRequired: false,
      },
      yearBuilt: this.internalJson.shipData?.yearBuilt,
      shipNameAkas: this.internalJson.shipData?.shipNameAkas || [],
    };

    const shipDataIsEqual = objectsAreEqual<TSubIShipData>(oldSubShipData, newShipData, true);

    if (shipDataIsEqual) return this;

    const shipData = this.internalJson.shipData;

    safeSet<IShipData>(shipData, "lineOperator", lineOperator);
    safeSet<IShipData>(shipData, "shipClass", shipClass);
    safeSet<IShipData>(shipData, "shipName", shipName);
    safeSet<IShipData>(shipData, "callSign", callSign);
    safeSet<IShipData>(shipData, "imoCode", imoCode);
    safeSet<IShipData>(shipData, "positionFormat", positionFormat);
    safeSet<IShipData>(shipData, "containersLengths", containersLengths);
    safeSet<IShipData>(shipData, "yearBuilt", yearBuilt);
    safeSet<IShipData>(shipData, "bay40sLocation", bay40sLocation);
    if (loa !== undefined) safeSet<IShipData>(shipData, "loa", loa);
    safeSet<IShipData>(shipData, "shipNameAkas", shipNameAkas);

    if (fA) {
      const featuresAllowed: IFeaturesAllowed = {
        slotCoolStowProhibited: fA.indexOf("slotCoolStowProhibited") >= 0,
        slotHazardousProhibited: fA.indexOf("slotHazardousProhibited") >= 0,
        slotConeRequired: fA.indexOf("slotConeRequired") >= 0,
      };
      safeSet<IShipData>(shipData, "featuresAllowed", featuresAllowed);
    }

    if (doEmitChange) {
      this.emitChange({ type: OVDChangesEnum.SHIP_DATA_GENERAL_INFO });

      if (!shipDataIsEqual && this.internalCloudInfo)
        BeaconServices.logEvents.notifyXhttp({
          eventEntity: LogEventEntitiesEnum.File,
          eventType: LogEventTypesEnum.Modified,
          subEvent: "VesselInfo",
          itemId: this.internalCloudInfo.fileId,
          ...securityModule.getBeaconMetadata(),
        });
    }

    return this;
  }

  setShipSize(prms: IEditSizeFields) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    const prevSizeSummary: ISizeSummary = { ...this.internalJson.sizeSummary };

    const createEmptyBayData = (isoBay: IBayPattern, level: BayLevelEnum): IBayLevelData => ({
      isoBay,
      level,
      infoByContLength: {},
      perSlotInfo: {},
      centerLineRow: prms.centerLineRow ? 1 : 0,
    });

    const newSizeSummary: ISizeSummary = {
      isoBays: prms.isoBays,
      centerLineRow: prms.centerLineRow ? 1 : 0,
      maxRow: prms.maxRow,
      minBelowTier: prms.minBelowTier,
      maxBelowTier: prms.maxBelowTier,
      minAboveTier: prms.minAboveTier,
      maxAboveTier: prms.maxAboveTier,
    };

    if (objectsAreEqual<ISizeSummary>(prevSizeSummary, newSizeSummary)) return this;

    const newCenterLineRow = prms.centerLineRow ? 1 : 0;

    // If isoBays changes, create/delete new bayData
    if (
      prms.isoBays !== prevSizeSummary.isoBays ||
      newCenterLineRow !== prevSizeSummary.centerLineRow
    ) {
      const currentBaysData = this.internalJson.baysData;

      const baysDataPerIsoBayAndLevel: IBaysDataPerIsoBayAndLevel = {};

      currentBaysData.forEach((bl) => {
        if (!baysDataPerIsoBayAndLevel[bl.isoBay]) baysDataPerIsoBayAndLevel[bl.isoBay] = {};

        baysDataPerIsoBayAndLevel[bl.isoBay][bl.level === BayLevelEnum.ABOVE ? "above" : "below"] =
          bl;
      });

      const newBaysData: IBayLevelData[] = [];

      for (let i = 1; i <= prms.isoBays; i += 2) {
        const isoBay = pad3(i);

        if (prms.hasAboveDeck) {
          const bayDataAbove = baysDataPerIsoBayAndLevel[isoBay]?.above;
          if (bayDataAbove !== undefined) {
            if (!newCenterLineRow) bayDataAbove.centerLineRow = 0;
            newBaysData.push(bayDataAbove);
          } else {
            newBaysData.push(createEmptyBayData(isoBay, BayLevelEnum.ABOVE));
          }
        }

        if (prms.hasBelowDeck) {
          const bayDataBelow = baysDataPerIsoBayAndLevel[isoBay]?.below;
          if (bayDataBelow !== undefined) {
            if (!newCenterLineRow) bayDataBelow.centerLineRow = 0;
            newBaysData.push(bayDataBelow);
          } else {
            newBaysData.push(createEmptyBayData(isoBay, BayLevelEnum.BELOW));
          }
        }
      }

      newBaysData.sort(
        sortByMultipleFields([
          { name: "isoBay", ascending: true },
          { name: "level", ascending: true },
        ]),
      );

      this.internalJson.baysData = newBaysData;
    }

    this.internalJson.sizeSummary = newSizeSummary;

    this.emitChange({ type: OVDChangesEnum.SHIP_SIZE });

    BeaconServices.logEvents.notifyXhttp({
      eventEntity: LogEventEntitiesEnum.File,
      eventType: LogEventTypesEnum.Modified,
      subEvent: "Size",
      itemId: this.internalCloudInfo.fileId,
      ...securityModule.getBeaconMetadata(),
    });

    return this;
  }

  setPairings(pairings: IBayLevelPairing[]) {
    const json = this.internalJson;

    if (json === undefined) return this;

    let emitChange = false;

    const pairingsMap = pairings.reduce((acc, p) => {
      acc.set(p.isoBay, p.pairedBay);
      return acc;
    }, new Map<IBayPattern, ForeAftEnum | undefined>());

    for (let i = 0; i < json.baysData.length; i++) {
      const bayData = json.baysData[i];
      const pairedBay = pairingsMap.get(bayData.isoBay);

      if (pairedBay !== undefined && bayData.pairedBay !== pairedBay) {
        bayData.pairedBay = pairedBay;
        emitChange = true;
      }
    }

    if (emitChange) this.emitChange({ type: OVDChangesEnum.ALL_BAYS });
    return this;
  }

  setLids(lidData: ILidData[]) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    if (arraysAreEqual<ILidData>(this.internalJson.lidData, lidData, true)) return this;

    const diffs = diffArrays<ILidData>(this.internalJson.lidData, lidData, true);

    const diffsBays: IBayPattern[] = [
      ...(diffs.added?.flatMap(getIsoBaysUnderLid) || []),
      ...(diffs.removed?.flatMap(getIsoBaysUnderLid) || []),
    ].filter((v, i, a) => a.indexOf(v) === i);

    this.internalJson.lidData = lidData;

    this.emitChange({
      type: OVDChangesEnum.LIDS,
      data: diffsBays,
    });

    BeaconServices.logEvents.notifyXhttp({
      eventEntity: LogEventEntitiesEnum.File,
      eventType: LogEventTypesEnum.Modified,
      subEvent: "HatchCovers",
      itemId: this.internalCloudInfo.fileId,
      ...securityModule.getBeaconMetadata(),
    });

    return this;
  }

  setVesselParts(vesselPartsData: IVesselParts[]) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    if (arraysAreEqual<IVesselParts>(this.internalJson.vesselPartsData, vesselPartsData, true))
      return this;

    this.internalJson.vesselPartsData = vesselPartsData;

    BeaconServices.logEvents.notifyXhttp({
      eventEntity: LogEventEntitiesEnum.File,
      eventType: LogEventTypesEnum.Modified,
      subEvent: "VesselParts",
      itemId: this.internalCloudInfo.fileId,
      ...securityModule.getBeaconMetadata(),
    });

    return this;
  }

  setOffsetBottomBases(offsetBottomBases: Array<IBayLevelOffsetBottomBase> | undefined) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    if (
      arraysAreEqual<IBayLevelOffsetBottomBase>(
        this.internalJson.shipData.masterCGs?.offsetBottomBases,
        offsetBottomBases,
        true,
      )
    )
      return this;

    this.internalJson.shipData.masterCGs.offsetBottomBases = offsetBottomBases;

    return this;
  }

  findBayInfo(isoBay: IBayPattern, level: BayLevelEnum): IBayLevelData | undefined {
    if (this.internalJson === undefined) return undefined;

    return this.internalJson.baysData.find((bl) => bl.isoBay === isoBay && bl.level === level);
  }

  replaceBayInfo(
    isoBay: IBayPattern,
    level: BayLevelEnum,
    bayDataSrc: IBayLevelData,
    doEmitChange = true,
  ) {
    const json = this.internalJson;
    if (json === undefined) return this;

    const bayData = cleanUpBay(bayDataSrc);

    const idx = json.baysData.findIndex((bl) => bl.isoBay === isoBay && bl.level === level);

    const prevBayData = idx >= 0 ? json.baysData[idx] : undefined;

    if (idx >= 0) {
      json.baysData[idx] = bayData;
    } else {
      json.baysData.push(bayData);
      json.baysData.sort(
        sortByMultipleFields([
          { name: "isoBay", ascending: true },
          { name: "level", ascending: true },
        ]),
      );
    }

    if (doEmitChange) {
      const dataChanged = [isoBay] as Array<IBayPattern>;

      if (bayDataSrc.pairedBay !== undefined || prevBayData?.pairedBay !== undefined) {
        const newPairedBayNumber = getPairedIsoBay(isoBay, bayDataSrc.pairedBay);

        const oldPairedBayNumber = getPairedIsoBay(isoBay, prevBayData?.pairedBay);

        if (newPairedBayNumber) dataChanged.push(newPairedBayNumber);
        if (oldPairedBayNumber) dataChanged.push(oldPairedBayNumber);
      }

      this.emitChange({
        type: OVDChangesEnum.BAY_INFO,
        data: dataChanged.filter((v, i, a) => a.indexOf(v) === i),
      });
    }

    return this;
  }

  replaceJson(json: IOpenVesselDefinitionV1) {
    if (this.internalJson === undefined) return this;
    this.internalJson = json;
    this.emitChange({ type: OVDChangesEnum.FULL });
    return this;
  }

  setCGsInfo(
    isoBay: IBayPattern,
    level: BayLevelEnum,
    perRowInfo: TBayRowInfo,
    infoByContLength: TRowInfoByLength,
  ) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    const idx = this.internalJson.baysData.findIndex(
      (bl) => bl.isoBay === isoBay && bl.level === level,
    );

    if (idx < 0) return this;

    if (
      objectsAreEqual(perRowInfo, this.internalJson.baysData[idx].perRowInfo) &&
      objectsAreEqual(infoByContLength, this.internalJson.baysData[idx].infoByContLength)
    ) {
      return this;
    }

    this.internalJson.baysData[idx].perRowInfo = perRowInfo;
    this.internalJson.baysData[idx].infoByContLength = infoByContLength;

    this.emitChange({
      type: OVDChangesEnum.CGS_INFO,
      data: [`${isoBay}-${level}`],
    });

    BeaconServices.logEvents.notifyXhttp({
      eventEntity: LogEventEntitiesEnum.File,
      eventType: LogEventTypesEnum.Modified,
      subEvent: "BayCGs",
      itemId: this.internalCloudInfo.fileId,
      ...securityModule.getBeaconMetadata(),
    });

    return this;
  }

  setMasterCGs(masterCGs: IMasterCGs) {
    if (this.internalJson === undefined || !this.internalCloudInfo) return this;

    if (objectsAreEqual<IMasterCGs>(this.internalJson.shipData.masterCGs, masterCGs)) return this;

    this.internalJson.shipData.masterCGs = masterCGs;

    this.emitChange({ type: OVDChangesEnum.MASTER_CGS });

    BeaconServices.logEvents.notifyXhttp({
      eventEntity: LogEventEntitiesEnum.File,
      eventType: LogEventTypesEnum.Modified,
      subEvent: "MasterCGs",
      itemId: this.internalCloudInfo.fileId,
      ...securityModule.getBeaconMetadata(),
    });

    return this;
  }

  async setFileState(
    newState: IFileState,
    comments?: string,
  ): Promise<IResponseModel<void> | undefined> {
    if (this.internalJson === undefined || !this.tvdId) return undefined;

    const oldState = this.tvdId.fileState;

    if (oldState === newState) return undefined;

    this.tvdId.fileState = newState;

    this.dispatchEvent(new CustomEvent("updatingDB", { detail: { isUpdating: true } }));

    const resp = await Services.files.changeFileState(this.tvdId.fileId, newState, comments);

    this.dispatchEvent(new CustomEvent("updatingDB", { detail: { isUpdating: false } }));

    if (resp.statusCode !== 200) {
      this.tvdId.fileState = oldState;
    } else {
      this.tvdId.fileState = newState;
      if (newState !== "DRAFT") this.tvdId.lastComment = comments;

      this.dispatchEvent(new CustomEvent("stateChanged", { detail: { oldState, newState } }));

      this.dispatchEvent(
        new CustomEvent<ICustomOVDChangeEvent>("jsonUpdated", {
          detail: { type: OVDChangesEnum.FILE_STATE },
        }),
      );
    }

    return resp;
  }

  async set3DViewParams(
    params: IVesselOneParametrizationFormElements,
    vesselParts: IVesselParts[] = [],
    offsetBottomBases: Array<IBayLevelOffsetBottomBase> = [],
  ): Promise<IResponseModel<void> | undefined> {
    if (this.internalJson === undefined || !this.tvdId) return undefined;

    this.dispatchEvent(new CustomEvent("updatingDB", { detail: { isUpdating: true } }));

    // This is the API call to update the 3D view params in the JSON file and the DynamoDB table
    const resp = await Services.files.update3DViewData(this.tvdId.fileId, {
      params, // to DynamoDB table
      vesselParts, // to JSON file
      offsetBottomBases, // to JSON file
    });

    // This is the local update of the 3D view params in the JSON file
    this.setVesselParts(vesselParts);
    this.setOffsetBottomBases(offsetBottomBases);

    this.tvdId.v3DParams = { params };

    this.dispatchEvent(new CustomEvent("updatingDB", { detail: { isUpdating: false } }));

    if (resp.statusCode === 200 || resp.statusCode === 204) {
      this.dispatchEvent(
        new CustomEvent<ICustomOVDChangeEvent>("jsonUpdated", {
          detail: { type: OVDChangesEnum.VESSEL_PARTS },
        }),
      );
    }

    return resp;
  }

  get originalJson() {
    return this.originalInternalJson;
  }

  get currentJson() {
    return this.internalJson;
  }

  get filenameSanitized() {
    return sanitizeString(this.internalName || "");
  }

  get filenameSanitizedForSE() {
    const shipData = this.internalJson?.shipData;
    function snUpp(s: string): string {
      return sanitizeString(s || "").toUpperCase();
    }

    const name = shipData
      ? `${snUpp(shipData.shipClass || "___")}.${snUpp(
          shipData.lineOperator || "___",
        )}.${snUpp(shipData.shipName || "___")}`
      : sanitizeString(this.internalName || "");

    return name;
  }

  get tvdId() {
    return this.internalCloudInfo;
  }

  set tvdId(cloudInfo: ITvdCloudFileMeta | undefined) {
    this.internalCloudInfo = cloudInfo;
  }

  get totalSlotsDefined(): number {
    if (this.internalJson === undefined) return 0;

    return this.internalJson.baysData.reduce(
      (acc, bayData) => acc + Object.keys(bayData.perSlotInfo || {}).length,
      0,
    );
  }

  get lcgVcgTcgAndPairings() {
    return this._lcgVcgTcgAndPairings;
  }

  restoreOriginalJson() {
    this.setJson(this.internalName || "", cloneObject(this.originalInternalJson));

    return this;
  }

  saveToCloud = async (originalSource?: TSourceFileCreated, vmdShipNameLatest?: string) => {
    if (this.readonlyMode) return;

    const json = this.internalJson;
    const shipData = json?.shipData;

    if (!json || !shipData?.shipName) return;

    const { cgStats: cgs } = new BayLcgVcgTcgAndPairings(json);

    const cgsPercentage = roundDec(
        ((cgs.definedLcgs + cgs.definedBottomBase + cgs.definedTcgs) /
          (cgs.countLcgs + cgs.countBottomBase + cgs.countTcgs)) *
          100,
        2,
      ),
      lcgsPercentage = roundDec((cgs.definedLcgs / cgs.countLcgs) * 100, 2),
      tcgsPercentage = roundDec((cgs.definedTcgs / cgs.countTcgs) * 100, 2),
      vcgsPercentage = roundDec((cgs.definedBottomBase / cgs.countBottomBase) * 100, 2);

    const ovdValidationExportStafError = this.ovdValidator
      .getLastResult(["exportStaf"])
      ?.some((r) => !r.isValid);

    const dataToSave = {
      name: shipData.shipName,
      vmdShipNameLatest,
      vmdShipNameDiffers: vmdShipNameLatest ? shipData.shipName !== vmdShipNameLatest : false,
      cgsPercentage,
      lcgsPercentage,
      tcgsPercentage,
      vcgsPercentage,
      imoCode: shipData.imoCode,
      callSign: shipData.callSign,
      shipClass: shipData.shipClass,
      lastModified: new Date(),
      shipNameAkas: shipData.shipNameAkas?.join("\n") || "",
      originalSource,
      ovdValidationExportStafError,
      data: JSON.stringify(json),
    };

    if (this.tvdId !== undefined) {
      // UPDATE;
      const { data } = await Services.files.update({
        fileId: this.tvdId.fileId,
        userSub: this.tvdId.userSub,
        organizationId: this.tvdId.organizationId,
        organizationName: securityModule.currentOrganizationName,
        state: this.tvdId.fileState,
        teus: calculateTeusFromSlots(json.baysData),
        hasHatchCovers: !!json.lidData?.length,
        ...dataToSave,
      });

      if (!data) {
        this.dispatchEvent(new CustomEvent("error"));
      } else {
        if (data?.state) {
          this.tvdId.fileState = data.state;
          this.tvdId.lastComment = data.lastComment;
        }
        this.tvdId.updatedBy = securityModule.userSub;
      }
    } else {
      // CREATE
      const apiResp = await Services.files.create({
        userSub: securityModule.userSub,
        organizationId: securityModule.currentOrganizationId,
        organizationName: securityModule.currentOrganizationName,
        state: "DRAFT",
        hasHatchCovers: !!json.lidData?.length,
        teus: calculateTeusFromSlots(json.baysData),
        ...dataToSave,
      });

      if (apiResp.data?.id !== undefined) {
        this.tvdId = {
          fileId: apiResp.data.id,
          userSub: securityModule.userSub,
          organizationId: securityModule.currentOrganizationId,
          organizationName: securityModule.currentOrganizationName,
          createdAt: new Date(),
          fileState: "DRAFT",
          shipNameAkaStr: "",
        };
      } else {
        this.tvdId = undefined;
        throw apiResp;
      }
    }
  };

  emitChange = async (changeInfo: ICustomOVDChangeEvent) => {
    window.clearTimeout(this._timeoutEmitter);

    const json = this.internalJson;
    if (!json) return;

    const oldState = this.tvdId?.fileState || "DRAFT";

    this._pendingChange = mergeOVDChanges(this._pendingChange, changeInfo);

    this._timeoutEmitter = window.setTimeout(() => {
      const mergedChange = this._pendingChange ?? changeInfo;
      this._pendingChange = undefined;

      this._lcgVcgTcgAndPairings = this.createLcgVcgTcgAndPairings(json);
      this.ovdValidator.validate(json, this._lcgVcgTcgAndPairings, undefined);

      // Set loading
      this.dispatchEvent(new CustomEvent("updatingDB", { detail: { isUpdating: true } }));

      // Emit change (OPTIMISTIC SAVE)
      this.dispatchEvent(
        new CustomEvent<ICustomOVDChangeEvent>("jsonUpdated", {
          detail: mergedChange,
        }),
      );

      this.dispatchEvent(new CustomEvent("jsonLoaded"));

      this.saveToCloud().then(() => {
        const newState: IFileState = this.tvdId?.fileState || "DRAFT";

        // Remove loading
        this.dispatchEvent(new CustomEvent("updatingDB", { detail: { isUpdating: false } }));

        if (oldState !== newState)
          this.dispatchEvent(
            new CustomEvent("stateChanged", {
              detail: { oldState, newState },
            }),
          );
      });
    }, 10);
  };
}

const ovdJsonStore = new OVDJsonStore();
export default ovdJsonStore;

function cleanUpBay(bayData: IBayLevelData) {
  deleteVerboseOptionalFalsyKeysInBayData(bayData, [
    "label20",
    "label40",
    "pairedBay",
    "reeferPlugs",
    "doors",
    "reeferPlugLimit",
    "athwartShip",
    "foreHatch",
    "ventilated",
    "heatSrcFore",
    "ignitionSrcFore",
    "quartersFore",
    "engineRmBulkFore",
  ]);

  return bayData;

  function deleteVerboseOptionalFalsyKeysInBayData(
    bayData: IBayLevelData,
    keys: (keyof IBayLevelData)[],
  ) {
    keys.forEach((key) => {
      if (!bayData[key]) delete bayData[key];
    });
  }
}

export interface IEditGeneralFields extends Record<string, unknown> {
  lineOperator?: string;
  shipClass: string;
  shipName?: string;
  callSign?: string;
  imoCode?: string;
  yearBuilt?: number;
  positionFormat: PositionFormatEnum;
  containersLengths: Array<TContainerLengths>;
  featuresAllowed: string[];
  shipNameAkaStr?: string;
  loa?: number;
  bay40sLocation?: ForeAftEnum;
}

export interface IEditSizeFields extends Record<string, unknown> {
  isoBays: number;
  centerLineRow?: boolean;
  maxRow?: number;
  maxAboveTier?: number;
  minAboveTier?: number;
  maxBelowTier?: number;
  minBelowTier?: number;
  hasAboveDeck: boolean;
  hasBelowDeck: boolean;
}

interface IBaysDataPerIsoBayAndLevel {
  [isoBay: IBayPattern]: {
    above?: IBayLevelData;
    below?: IBayLevelData;
  };
}

export interface ITvdCloudFileMeta {
  fileId: string;
  userSub: string;
  organizationId: string;
  organizationName: string;
  fileState: IFileState;
  createdAt?: Date;
  updatedAt?: Date;
  updatedBy?: string;
  fromBvoName?: string;
  lastComment?: string;
  v3DParams?: {
    params: IVesselOneParametrizationFormElements;
  };
  shipNameAkaStr: string;
}

export interface ICGsInfo {
  lcgValues: ValuesSourceEnum;
  tcgValues: ValuesSourceEnum;
  vcgValues: ValuesSourceEnum;
  lcgDataFilled: number;
  tcgDataFilled: number;
  vcgDataFilled: number;
}

export enum OVDChangesEnum {
  SHIP_DATA_GENERAL_INFO = "SHIP_DATA_GENERAL_INFO",
  SHIP_SIZE = "SHIP_SIZE",
  ALL_BAYS = "ALL_BAYS",
  LIDS = "LIDS",
  BULKHEADS = "BULKHEADS",
  LASHING_BRIDGES = "LASHING_BRIDGES",
  VESSEL_PARTS = "VESSEL_PARTS",
  BAY_INFO = "BAY_INFO",
  CGS_INFO = "CGS_INFO",
  MASTER_CGS = "MASTER_CGS",
  FILE_STATE = "FILE_STATE",
  FULL = "FULL",
}

export type ICustomOVDChangeEvent =
  | {
      type: OVDChangesEnum;
      data?: never;
    }
  | {
      type: OVDChangesEnum.BAY_INFO | OVDChangesEnum.CGS_INFO | OVDChangesEnum.LIDS;
      data: Array<`${IBayPattern}-${BayLevelEnum}`> | Array<IBayPattern>;
    };

interface IOvdJsonStoreEventMap {
  jsonLoaded: Event;
  /** The file was repaired while opening (row 00 conflicts, misplaced 40'+). See `repairOvd` */
  ovdRepaired: CustomEvent<IRepairOvdResult>;
  jsonUpdated: CustomEvent<ICustomOVDChangeEvent>;
  stateChanged: CustomEvent<{
    detail: { oldState: IFileState; newState: IFileState };
  }>;
  updatingDB: CustomEvent<{ detail: { isUpdating: boolean } }>;
  error: CustomEvent<Error>;
}

interface IOvdJsonStoreEventTarget extends EventTarget {
  addEventListener<K extends keyof IOvdJsonStoreEventMap>(
    type: K,
    listener: (ev: IOvdJsonStoreEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void;
}
